import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { de, enUS, ja } from "date-fns/locale";
import { Pencil, UserPlus } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { getProfileDisplayName } from "@/lib/profileUtils";
import type { OpenInspectionRow } from "@/hooks/useOpenInspectionsQuery";

interface OpenInspectionsTableProps {
  inspections: OpenInspectionRow[];
  onAssign: (inspection: OpenInspectionRow) => void;
  onEdit: (inspectionId: string) => void;
}

const localeFor = (lang: string) => (lang === "en" ? enUS : lang === "ja" ? ja : de);

/**
 * Table of open inspections (TES-506 §2). Columns: Prüfobjekt/Position, Vorlage,
 * Fällig am, Zustand, Zugewiesen an, Aktion (Zuordnen / Editieren).
 */
export function OpenInspectionsTable({ inspections, onAssign, onEdit }: OpenInspectionsTableProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = localeFor(i18n.language);

  if (inspections.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground py-8 text-center"
        data-testid="open-inspections-empty"
      >
        {t("openInspections.tableEmpty")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table data-testid="open-inspections-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t("openInspections.columns.position")}</TableHead>
            <TableHead>{t("openInspections.columns.template")}</TableHead>
            <TableHead>{t("openInspections.columns.dueDate")}</TableHead>
            <TableHead>{t("openInspections.columns.status")}</TableHead>
            <TableHead>{t("openInspections.columns.assignee")}</TableHead>
            <TableHead className="text-right">{t("openInspections.columns.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inspections.map((inspection) => {
            const assigneeName = inspection.assigned_profile
              ? getProfileDisplayName(inspection.assigned_profile)
              : null;
            return (
              <TableRow key={inspection.id} data-testid={`open-inspection-row-${inspection.id}`}>
                <TableCell className="font-medium">
                  {inspection.position_name ?? t("openInspections.noPosition")}
                </TableCell>
                <TableCell>
                  {inspection.checklist_name ?? t("dashboard.unknownChecklist")}
                </TableCell>
                <TableCell>
                  {inspection.due_date
                    ? format(new Date(inspection.due_date), "d. MMM yyyy", { locale: dateLocale })
                    : t("openInspections.noDueDate")}
                </TableCell>
                <TableCell>
                  <StatusBadge status={inspection.status} />
                </TableCell>
                <TableCell data-sentry-mask>
                  {assigneeName ?? (
                    <span className="text-warning">{t("openInspections.unassigned")}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onAssign(inspection)}
                      data-testid={`open-inspection-assign-${inspection.id}`}
                    >
                      <UserPlus className="h-3.5 w-3.5 md:mr-1.5" />
                      <span className="hidden md:inline">{t("openInspections.assign")}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(inspection.id)}
                      data-testid={`open-inspection-edit-${inspection.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 md:mr-1.5" />
                      <span className="hidden md:inline">{t("openInspections.edit")}</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
